import React, { useCallback, useEffect, useState } from "react";
import { FaPlus, FaSitemap, FaTrash, FaUserCog } from "react-icons/fa";
import { useConfirm, useNotify } from "../../components/ConfirmModal";
import AssignWizard from "./AssignWizard";
import ItemDrawer from "./ItemDrawer";
import { AreaChip, Avatar, Empty, ItemCard, PersonSelect } from "./WorkUi";
import { errorMessage, fmtShortDate } from "./workApi";
import "../dm/dm.css";
import "./work.css";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "late", label: "Late" },
  { key: "review", label: "Waiting review" },
];

const matches = (filter) => (item) => {
  if (filter === "open") return !item.Is_Closed;
  if (filter === "late") return item.Is_Overdue;
  if (filter === "review") return item.Status === "In Review";
  return true;
};

function Lane({ lane, api, meta, today, filter, onOpen, onEdit, onChanged }) {
  const notify = useNotify();
  const confirm = useConfirm();
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const items = lane.items.filter(matches(filter));

  const quickAdd = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.createItem(lane.Stream_Id, { Title: title.trim(), Executor_Id: lane.Lead_Id || undefined });
      setTitle("");
      onChanged();
    } catch (err) {
      notify.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const removeLane = async () => {
    if (!(await confirm({ title: "Remove lane", message: `Remove the ${lane.Stream_Name} lane?` }))) return;
    try {
      await api.deleteStream(lane.Stream_Id);
      onChanged();
    } catch (err) {
      notify.error(errorMessage(err));
    }
  };

  return (
    <section className="wk-lane" style={{ "--wk-area": lane.Area_Color }} aria-label={`${lane.Stream_Name} lane`}>
      <div className="wk-lane__head">
        <h3 className="wk-lane__title">
          <AreaChip name={lane.Stream_Name} color={lane.Area_Color} icon={lane.Area_Icon} />
        </h3>
        {lane.Can_Manage ? (
          <span>
            <button type="button" className="dmp-link" onClick={() => onEdit(lane.Area_Id)} aria-label={`Change people for ${lane.Stream_Name}`}>
              <FaUserCog aria-hidden="true" />
            </button>{" "}
            <button type="button" className="dmp-link" onClick={removeLane} aria-label={`Remove ${lane.Stream_Name} lane`}>
              <FaTrash aria-hidden="true" />
            </button>
          </span>
        ) : null}
      </div>
      <div className="wk-lane__people">
        <span>
          <Avatar name={lane.Lead_Name} size={20} /> Lead: {lane.Lead_Name || "—"}
        </span>
        <span>Reviewer: {lane.Reviewer_Name || "Project owner"}</span>
        {lane.Due_Date ? <span className={lane.Is_Overdue ? "wk-due is-late" : ""}>Deadline {fmtShortDate(lane.Due_Date)}</span> : null}
      </div>
      <div className="wk-lane__stats">
        <span className="wk-count">{lane.Counts.open} open</span>
        {lane.Counts.overdue ? <span className="wk-count wk-count--red">{lane.Counts.overdue} late</span> : null}
        {lane.Counts.in_review ? <span className="wk-count wk-count--violet">{lane.Counts.in_review} in review</span> : null}
        {lane.Counts.blocked ? <span className="wk-count wk-count--amber">{lane.Counts.blocked} stuck</span> : null}
        <span className="wk-count">{lane.Progress_Pct}% done</span>
      </div>
      <div className="wk-lane__items">
        {items.length ? (
          items.map((item) => <ItemCard key={item.Item_Id} item={item} today={today} onOpen={onOpen} showProject={false} />)
        ) : (
          <div className="wk-muted" style={{ padding: "8px 0" }}>
            {lane.items.length ? "Nothing matches this filter." : "No deliverables yet."}
          </div>
        )}
      </div>
      <form className="wk-quick-add" onSubmit={quickAdd}>
        <label className="wk-sr-only" htmlFor={`qa-${lane.Stream_Id}`}>
          Add a deliverable to {lane.Stream_Name}
        </label>
        <input
          id={`qa-${lane.Stream_Id}`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={`+ Add ${String(lane.Default_Item_Type || "item").toLowerCase()}`}
          maxLength={200}
        />
        <button type="submit" className="dmp-btn dmp-btn--sm" disabled={busy || !title.trim()}>
          Add
        </button>
      </form>
    </section>
  );
}

/** Project plan: one lane per functional area with its lead, reviewer and deliverables. */
function ProjectPlan({ api, projectType, projectId, initialItemId }) {
  const notify = useNotify();
  const [plan, setPlan] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [wizard, setWizard] = useState(null);
  const [openItem, setOpenItem] = useState(initialItemId || null);
  const [filter, setFilter] = useState("all");
  const [ownerEdit, setOwnerEdit] = useState(false);

  const load = useCallback(async () => {
    try {
      setPlan(await api.plan(projectType, projectId));
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load the plan"));
    }
  }, [api, projectType, projectId]);

  useEffect(() => {
    load();
    api
      .meta({ project_type: projectType, project_id: projectId })
      .then(setMeta)
      .catch(() => setMeta(null));
  }, [api, load, projectType, projectId]);

  const changeOwner = async (userId) => {
    if (!userId) return;
    try {
      setPlan(await api.setOwner(projectType, projectId, userId));
      setOwnerEdit(false);
      notify.success("Project owner updated");
    } catch (err) {
      notify.error(errorMessage(err));
    }
  };

  if (!plan) {
    return <div className="dmp-card">{error ? <div className="dmp-error">{error}</div> : <span className="wk-muted">Loading plan…</span>}</div>;
  }

  const { project, streams } = plan;

  return (
    <div className="wk">
      <div className="dmp-card">
        <div className="wk-plan-head">
          <div>
            <div className="wk-owner">
              <Avatar name={project.Owner_Name} size={34} />
              <div>
                <div className="wk-muted" style={{ fontSize: 12 }}>
                  Project owner (accountable, final review)
                </div>
                {ownerEdit ? (
                  <PersonSelect people={meta?.people} value={project.Owner_Id} onChange={changeOwner} blank="Pick the owner" />
                ) : (
                  <strong>
                    {project.Owner_Name || "Not set"}{" "}
                    {plan.can_change_owner ? (
                      <button type="button" className="dmp-link" onClick={() => setOwnerEdit(true)}>
                        Change
                      </button>
                    ) : null}
                  </strong>
                )}
              </div>
            </div>
          </div>
          <div className="wk-chip-row" role="group" aria-label="Filter items" style={{ marginBottom: 0 }}>
            {FILTERS.map((f) => (
              <button key={f.key} type="button" className="wk-chip" aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
                {f.label}
              </button>
            ))}
          </div>
          <div className="dmp-actions">
            <span className="wk-count">{project.Progress_Pct}% done</span>
            <button type="button" className="dmp-btn dmp-btn--new" onClick={() => setWizard({})}>
              <FaPlus aria-hidden="true" /> Assign work
            </button>
          </div>
        </div>
        {error ? <div className="dmp-error">{error}</div> : null}

        {streams.length ? (
          <div className="wk-lanes">
            {streams.map((lane) => (
              <Lane
                key={lane.Stream_Id}
                lane={lane}
                api={api}
                meta={meta}
                today={meta?.today}
                filter={filter}
                onOpen={(item) => setOpenItem(item.Item_Id)}
                onEdit={(areaId) => setWizard({ areaId })}
                onChanged={load}
              />
            ))}
          </div>
        ) : (
          <Empty icon={FaSitemap} title="No work assigned yet">
            Split this project into areas like Video, Media or Campaign, then give each a lead, a reviewer and dates.
            <div style={{ marginTop: 12 }}>
              <button type="button" className="dmp-btn wk-btn-lg" onClick={() => setWizard({})}>
                <FaPlus aria-hidden="true" /> Assign work
              </button>
            </div>
          </Empty>
        )}
      </div>

      {wizard ? (
        <AssignWizard
          api={api}
          projectType={projectType}
          projectId={projectId}
          meta={meta}
          plan={plan}
          initialAreaId={wizard.areaId}
          onClose={() => setWizard(null)}
          onDone={(p) => setPlan(p)}
        />
      ) : null}
      {openItem ? <ItemDrawer api={api} itemId={openItem} meta={meta} onClose={() => setOpenItem(null)} onChanged={load} /> : null}
    </div>
  );
}

export default ProjectPlan;
